import React from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';

/** @import { FieldError, Merge, FieldErrorsImpl } from 'react-hook-form'; */

/**
 * @typedef {string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined} Message
 */

/**
 * Recursive function to extract error message from nested error objects.
 * @param {Message} message - The error object which may contain nested errors.
 * @param {number} [depth] - The current depth of recursion to prevent infinite loops.
 * @returns {string} The extracted error message or an empty string if none found.
 */
function extractErrorMessage(message, depth = 0) {
  if (typeof message === 'string') {
    return message;
  }

  if (message?.message && depth < 5) {
    return extractErrorMessage(message.message, depth + 1);
  }

  return '';
}

/**
 * Error component to display error messages.
 *
 * @param {object} props
 * @param {Message} [props.message] - The error object containing the message.
 * @param {string|null} [props.className=null] - Additional class name(s) for styling.
 * @param {string} [props.id] - The id for the error message element
 */
const ErrorMsg = ({ message = '', className = null, id }) => (
  <div
    className={cx(styles.errorMessage, className)}
    data-testid="error-message"
    role="alert"
    aria-live="assertive"
    id={id}
  >
    {extractErrorMessage(message)}
  </div>
);

export { ErrorMsg };
